import * as vp from 'vopay';
import { FunctionComponent, JSX, useEffect } from 'react';
import moment from 'moment';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useFetchTransactionsHistory from '@/hooks/useApi/useFetchTransactionsHistory';

const { Table, Box, SubTitle, Badge, formattedCurrency } = vp;

type DataProps = {
  transactionId: string;
  hasErrorInfo: boolean;
  failureReason: string;
};

type FC = FunctionComponent;

type KeyValuePair = {
  display: string;
  value: string | JSX.Element;
  visible?: boolean;
};

type TablePropsHandlers = {
  transactionDetails: () => vp.TableProps;
  errorInfo: () => vp.TableProps;
};
type URLParams = {
  TransactionType: string;
  TransactionID: string;
  Currency: string;
};

const OtherTransactionInfo: FC = () => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.OtherTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');
  const { state } = useLocation<DataProps>();
  const { TransactionID } = useParams<URLParams>();
  const currentDate = moment().utc().format('YYYY-MM-DD');
  const { transactions, fetchTransactionsHistory } = useFetchTransactionsHistory();
  const transactionHistory = transactions.filter((data) => data.TransactionID === TransactionID);

  useEffect(() => {
    fetchTransactionsHistory({
      StartDateTime: moment().subtract(5, 'years').format('YYYY-MM-DD'),
      EndDateTime: currentDate,
      TransactionID,
    });
  }, [TransactionID, currentDate]);

  return (
    <div>
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('transactionDetailsTitle') || ''} />
      </Box>
      <Table {...getTableProps('transactionDetails')} />
      {state?.hasErrorInfo && (
        <>
          <Box mt="lg" pt="md" mb="md">
            <SubTitle subTitle={t('errorInfoTitle') || ''} />
          </Box>
          <Table {...getTableProps('errorInfo')} />
        </>
      )}
    </div>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      transactionDetails: () => {
        const accountDetailPairs: KeyValuePair[] = [
          {
            display: t('transactionDetails.transactionID'),
            value: transactionHistory.map((data) => data.TransactionID).pop() || '',
            visible: true,
          },
          {
            display: t('transactionDetails.transactionDate'),
            value: transactionHistory.map((data) => data.TransactionDateTime).pop() || '',
            visible: true,
          },
          {
            display: t('transactionDetails.lastModified'),
            value: transactionHistory.map((data) => data.LastModified).pop() || '',
            visible: true,
          },
          {
            display: t('transactionDetails.type'),
            value:
              tGlobal(
                `Transaction.TransactionHistory.transactionTypes.${
                  transactionHistory.map((data) => data.TransactionType).pop() || ''
                }`,
                { defaultValue: transactionHistory.map((data) => data.TransactionType).pop() || '' },
              ) || '',
            visible: true,
          },
          {
            display: t('transactionDetails.status'),
            value: (
              <Badge
                label={
                  vp.capitalizeFirstLetterOfWords(
                    tGlobal(
                      `Transaction.TransactionHistory.statuses.${
                        transactionHistory.map((data) => data.TransactionStatus).pop() || ''
                      }`,
                      { defaultValue: transactionHistory.map((data) => data.TransactionStatus).pop() || '' },
                    ),
                  ) || ''
                }
                hasIcon
                color={vp.GetColorStatus(transactionHistory.map((data) => data.TransactionStatus).pop() || '')}
              />
            ),
            visible: true,
          },
          {
            display: t('transactionDetails.debitAmount'),
            value: `$${formattedCurrency(transactionHistory.map((data) => data.DebitAmount).pop() || '')}`,
            visible: true,
          },
          {
            display: t('transactionDetails.creditAmount'),
            value: `$${formattedCurrency(transactionHistory.map((data) => data.CreditAmount).pop() || '')}`,
            visible: true,
          },
          {
            display: t('transactionDetails.holdAmount'),
            value: `$${formattedCurrency(transactionHistory.map((data) => data.HoldAmount).pop() || '')}`,
            visible: true,
          },
          {
            display: t('transactionDetails.currency'),
            value: transactionHistory.map((data) => data.Currency).pop() || '',
            visible: true,
          },
          {
            display: t('transactionDetails.notes'),
            value: transactionHistory.map((data) => data.Notes).pop() || '',
            visible: !!transactionHistory.map((data) => data.Notes).pop(),
          },
        ];

        const accountInfoProps: vp.TableProps = {
          name: 'transaction-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('transactionDetails.transactionDetail') || '' },
            detailValue: { header: '' },
          },
          rows: accountDetailPairs
            .filter(({ visible }) => visible)
            .map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
        };

        return accountInfoProps;
      },
      errorInfo: () => {
        const ErrorInfoPairs: KeyValuePair[] = [
          {
            display: t('errorInfo.failureDateTime'),
            value: transactionHistory.map((data) => data.LastModified).pop() || '',
          },
          {
            display: t('errorInfo.amount'),
            value: `$${formattedCurrency(transactionHistory.map((data) => data.DebitAmount).pop() || '')}`,
          },
          {
            display: t('errorInfo.failureReason'),
            value: state.failureReason,
          },
        ];

        const ErrorInfoProps: vp.TableProps = {
          name: 'error-info',
          variant: 'pair',
          columns: {
            detailKey: { header: t('errorInfo.errorInformation') || '' },
            detailValue: { header: '' },
          },
          rows: ErrorInfoPairs.map(({ display, value }) => ({
            detailKey: display,
            detailValue: value,
          })),
        };

        return ErrorInfoProps;
      },
    };

    return handlers[handler]();
  }
};

export default OtherTransactionInfo;
